import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { priceOnFinish, priceTypingProcess } from '../../../supplementary_components/priceInputDelegate';
import { resolveDriverPayoutTrucks } from '../../../supplementary_components/driverPayoutUtils';

/** Match InputFieldDelegate Label + TextField spacing/look. */
const Wrapper = styled.div`
    width: 100%;
`;

const FieldLabel = styled.div`
    font-size: 1.1rem;
    font-weight: 500;
    padding: 0.8rem 0;
    /* Always top-spaced: this editor sits after Payout, same as non-first form Labels */
    margin-top: 1rem;
    color: var(--dark-gray);
`;

const FieldInput = styled.input`
    box-sizing: border-box;
    -moz-box-sizing: border-box;
    -webkit-box-sizing: border-box;
    font-family: var(--main-font-family);
    width: 100%;
    font-size: 1rem;
    font-weight: 600;
    padding: 0.7rem;
    color: var(--dark-gray);
    border: none;
    box-shadow: var(--button-outline);
    -moz-box-shadow: var(--button-outline);
    -webkit-box-shadow: var(--button-outline);
    transition: 0.08s;
    border-radius: 0.4rem;
    background: var(--bg);

    &:focus {
        outline: rgba(48, 192, 140, 0.5) solid 0.25rem !important;
    }

    &::placeholder {
        color: var(--light-gray);
        opacity: 0.5;
    }
    &::-ms-input-placeholder {
        color: var(--light-gray);
        opacity: 0.5;
    }
`;

const EDITOR_ID = 'driverPayoutEditor';

function parseMoneyOrNull(raw) {
    if (raw == null || String(raw).trim() === '') return null;
    const cleaned = String(raw).replace(/[^0-9.]/g, '');
    if (cleaned === '' || cleaned === '.') return null;
    const parsed = parseFloat(cleaned);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function formatForInput(amount) {
    if (amount === null || amount === undefined || amount === '') return '';
    return priceOnFinish(`${amount}`);
}

function trucksToMap(trucks, amounts) {
    const map = {};
    for (const truck of trucks) {
        const payout = amounts[truck.truckId];
        map[truck.truckId] = {
            payout: payout === undefined ? null : payout,
            truckNumber: truck.truckNumber,
        };
    }
    return map;
}

function syncEditorAttribute(trucks, amounts) {
    const container = document.getElementById(EDITOR_ID);
    if (!container) return;
    container.setAttribute('data-driver-payout-json', JSON.stringify(trucksToMap(trucks, amounts)));
}

export const DriverPayoutEditor = ({ data, driverPayout, trucks: trucksProp }) => {
    const trucks = (Array.isArray(trucksProp) && trucksProp.length > 0)
        ? trucksProp
        : resolveDriverPayoutTrucks({ ...data, driverPayout: driverPayout ?? data?.driverPayout });
    const [amounts, setAmounts] = useState(() => {
        const init = {};
        for (const t of trucks) {
            init[t.truckId] = t.payout;
        }
        return init;
    });
    const [displayValues, setDisplayValues] = useState(() => {
        const init = {};
        for (const t of trucks) {
            init[t.truckId] = formatForInput(t.payout);
        }
        return init;
    });

    useEffect(() => {
        const nextAmounts = {};
        const nextDisplay = {};
        for (const t of trucks) {
            nextAmounts[t.truckId] = t.payout;
            nextDisplay[t.truckId] = formatForInput(t.payout);
        }
        setAmounts(nextAmounts);
        setDisplayValues(nextDisplay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(trucks.map((t) => `${t.truckId}:${t.payout}`))]);

    useEffect(() => {
        syncEditorAttribute(trucks, amounts);
    }, [amounts, trucks]);

    if (!trucks.length) {
        return null;
    }

    return (
        <Wrapper id={EDITOR_ID}>
            {trucks.map((truck) => {
                const key = truck.truckId;
                const label = trucks.length === 1 ? 'Driver Payout' : truck.truckNumber;
                return (
                    <div key={key}>
                        <FieldLabel>{label}</FieldLabel>
                        <FieldInput
                            data-driver-payout-truck={key}
                            data-truck-number={truck.truckNumber}
                            value={displayValues[key] ?? ''}
                            inputMode="decimal"
                            autoComplete="off"
                            placeholder="Not set"
                            onChange={(e) => {
                                const typed = priceTypingProcess(e.target.value);
                                const parsed = parseMoneyOrNull(typed);
                                setDisplayValues((prev) => ({ ...prev, [key]: typed }));
                                setAmounts((prev) => {
                                    const next = { ...prev, [key]: parsed };
                                    syncEditorAttribute(trucks, next);
                                    return next;
                                });
                            }}
                            onBlur={(e) => {
                                const parsed = parseMoneyOrNull(e.target.value);
                                setAmounts((prev) => {
                                    const next = { ...prev, [key]: parsed };
                                    syncEditorAttribute(trucks, next);
                                    return next;
                                });
                                setDisplayValues((prev) => ({
                                    ...prev,
                                    [key]: parsed == null ? '' : formatForInput(parsed),
                                }));
                            }}
                        />
                    </div>
                );
            })}
        </Wrapper>
    );
};

export function processDriverPayoutChanges() {
    const container = document.getElementById(EDITOR_ID);
    if (!container) return null;

    // Prefer live DOM values so Save works even if the last field was not blurred.
    const truckInputs = container.querySelectorAll('[data-driver-payout-truck]');
    if (truckInputs.length > 0) {
        const map = {};
        truckInputs.forEach((input) => {
            const truckId = input.getAttribute('data-driver-payout-truck');
            map[truckId] = {
                payout: parseMoneyOrNull(input.value),
                truckNumber: input.getAttribute('data-truck-number') || truckId,
            };
        });
        return { driverPayout: map };
    }

    const raw = container.getAttribute('data-driver-payout-json');
    if (!raw) return null;
    try {
        return { driverPayout: JSON.parse(raw) };
    } catch (e) {
        return null;
    }
}
